import { createFileRoute, Link } from "@tanstack/react-router";

import { Footer, Nav } from "../components/lidoo/chrome";
import { addressLines, COMPANY } from "../lib/company";

// One page rather than two. The identification block that art. 3:15d BW
// requires opens the terms, so a visitor, a beach club owner and a regulator
// all find the same thing in the same place, and the footer needs one link
// instead of a row of them.
//
// Written in the same plain register as the privacy notice. Terms nobody can
// read are terms nobody agreed to, and a Dutch court will read an unclear
// clause against the party that wrote it (art. 6:238 lid 2 BW).
export const Route = createFileRoute("/terms")({
  head: () => ({
    meta: [
      { title: "Terms and who we are | sunbedly" },
      {
        name: "description",
        content:
          "Who runs sunbedly, what the service is and is not, how listings and prices work, and the terms that apply when you use the site.",
      },
    ],
  }),
  component: TermsPage,
});

const UPDATED = "27 July 2026";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border-t border-[#1C2430]/10 py-8">
      <h2 className="li-serif text-2xl text-[#1C2430]">{title}</h2>
      <div className="mt-3 max-w-[68ch] space-y-3 text-[15px] leading-relaxed text-[#5B6472]">
        {children}
      </div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:gap-4">
      <dt className="li-mono w-40 shrink-0 text-[11px] uppercase tracking-widest text-[#5B6472]">
        {label}
      </dt>
      <dd className="text-[15px] text-[#1C2430]">{children}</dd>
    </div>
  );
}

export function TermsPage() {
  const mail = (
    <a href={`mailto:${COMPANY.email}`} className="text-[#2C4FD6] underline underline-offset-4">
      {COMPANY.email}
    </a>
  );

  return (
    <div className="min-h-dvh bg-[#F2EFE9]">
      <Nav />
      <main className="mx-auto max-w-6xl px-5 pb-24">
        <div className="pt-12">
          <p className="li-mono text-[11px] uppercase tracking-widest text-[#2C4FD6]">Terms</p>
          <h1 className="li-serif mt-3 max-w-[22ch] text-4xl leading-[1.05] text-[#1C2430] md:text-6xl">
            Who we are and what we do
          </h1>
          <p className="li-mono mt-4 text-[11px] text-[#5B6472]">Last updated {UPDATED}</p>
        </div>

        {/* The identification block. First thing on the page on purpose: it is
            the answer to "who am I actually dealing with", which is the
            question a club owner has before any other. */}
        <section className="mt-10 rounded-2xl border border-[#1C2430]/10 bg-white/70 p-6">
          <h2 className="li-display text-lg font-bold tracking-tight text-[#1C2430]">
            The business behind sunbedly
          </h2>
          <dl className="mt-5 space-y-3">
            <Row label="Trade name">{COMPANY.tradeName}</Row>
            <Row label="Registered as">
              {COMPANY.legalName} ({COMPANY.legalForm})
            </Row>
            <Row label="Based in">
              {addressLines().map((line) => (
                <span key={line} className="block">
                  {line}
                </span>
              ))}
            </Row>
            <Row label="Chamber of commerce">{COMPANY.kvk}</Row>
            {COMPANY.vat ? <Row label="VAT number">{COMPANY.vat}</Row> : null}
            <Row label="Email">{mail}</Row>
          </dl>
        </section>

        <Section title="What sunbedly is">
          <p>
            sunbedly is a directory. We describe beach clubs, publish what they charge where they
            publish it, and put you in touch with them. That is the whole service.
          </p>
          <p>
            <strong className="text-[#1C2430]">We are not the seller.</strong> We do not own,
            operate or represent any beach club on this site. We are not their agent and we do not
            act on their behalf. When you reserve a sunbed, your agreement is with the club and
            nobody else.
          </p>
          <p>
            <strong className="text-[#1C2430]">We handle no money.</strong> There is no checkout
            here and no payment details anywhere in this system. You pay the club, at the club, and
            we take no commission from what you pay.
          </p>
          <p>
            <strong className="text-[#1C2430]">We hold no inventory.</strong> We do not know how
            many beds a club has free today. Nothing on this site is a confirmed reservation until
            the club itself confirms it to you.
          </p>
        </Section>

        <Section title="Prices and opening times">
          <p>
            Where a club publishes a rate, we show it and we say what it covers. Where it does not,
            we say so rather than guess. A price on this site is what the club published at the
            moment we checked, and the date we checked is printed on the page.
          </p>
          <p>
            Beach club rates move with the season, the row you sit in and the day of the week. Treat
            what you see here as a guide and confirm the amount with the club before you go. If a
            price here turns out to be wrong, the club's own price is the one that counts, and we
            will correct ours as soon as we know.
          </p>
        </Section>

        <Section title="Sending a request">
          <p>
            The form on a club page takes a date, a number of sunbeds, your name and a way to reach
            you. It writes the message for you in the club's own language and hands you to the
            club's WhatsApp or email so you send it yourself.
          </p>
          <p>
            We keep a copy so the club can see the request in their dashboard. That is all it is: a
            question. It is not a booking, it does not oblige the club to keep anything free for
            you, and it does not create any agreement between you and us. What we do with the
            details you type is set out in our{" "}
            <Link to="/privacy" className="text-[#2C4FD6] underline underline-offset-4">
              privacy notice
            </Link>
            .
          </p>
        </Section>

        <Section title="How we order results">
          <p>
            When you look at a list of clubs, this is the order and why. Clubs that pay for
            placement come first and are labelled as such on the card, so you always know when
            money is the reason something is at the top. After that come clubs we have visited
            ourselves, then clubs that publish a price, then clubs with more reviews on Google.
            Everything after that is alphabetical.
          </p>
          <p>
            You can override the whole thing with the sort control, and the filters count what they
            will give you before you click. We do not rank clubs by anything they do not know
            about.
          </p>
        </Section>

        <Section title="If you run a club listed here">
          <p>
            Your listing is free, carries no advertising and costs you nothing. We built it from
            what your business publishes, from the official Google Places API, and from our own
            research. We have never bought a list, and we do not scrape Instagram, TripAdvisor or
            Google Maps.
          </p>
          <p>
            <strong className="text-[#1C2430]">Wrong details?</strong> Email {mail} and we correct
            them, usually the same day.
          </p>
          <p>
            <strong className="text-[#1C2430]">Want it gone?</strong> Same address, and the page
            comes down. There is no notice period and no contract to end, because you never signed
            one.
          </p>
          <p>
            <strong className="text-[#1C2430]">Want to manage it?</strong> The dashboard link we
            send you lets you correct your own details, add photos and enter phone bookings. Use it
            for your own club only. We can withdraw access if a listing is used to post something
            that is not true, not yours, or not lawful, and we will tell you why.
          </p>
        </Section>

        <Section title="Reviews and photos you send us">
          <p>
            Write about a visit you actually made. No invented visits, no reviews of your own
            business, no reviews of a competitor, nothing abusive, and no personal details of staff.
            Upload only photographs you took yourself or have the right to use, and only of the club
            they belong to. We read every upload before it appears.
          </p>
          <p>
            By sending us a review or a photo you let us publish it on sunbedly and show it in
            search results, for as long as the listing exists. You keep the copyright. Ask us and we
            take yours down.
          </p>
          <p>
            We remove anything that breaks these rules, and we will say so rather than delete
            quietly. We do not edit a review to change what it says, and we do not remove one just
            because a club dislikes it.
          </p>
        </Section>

        <Section title="Ratings from Google">
          <p>
            Star ratings and review counts on this site come from the Google Places API and are
            labelled with Google's name wherever they appear. They are Google's numbers, not ours,
            and we do not adjust them. Photos from that source carry their photographer's name for
            the same reason.
          </p>
        </Section>

        <Section title="Our own content">
          <p>
            The club write-ups, the guides, the design and the sunbedly name are ours. Link to us as
            much as you like. Copying the descriptions or the catalogue wholesale is not on, and
            neither is automated harvesting of the site.
          </p>
        </Section>

        <Section title="What we are and are not responsible for">
          <p>
            We are responsible for this site: for describing clubs honestly, for saying where our
            information came from, and for correcting it when it is wrong.
          </p>
          <p>
            We are not responsible for what happens at the club. If a club is closed, full, more
            expensive than we said, or simply not what you hoped, that sits between you and them. We
            are not liable for the club's performance, for a reservation they did not honour, or for
            anything you agree with them directly.
          </p>
          <p>
            Nothing here limits liability for intent or deliberate recklessness on our part, for
            death or personal injury, or any other liability that Dutch law does not allow to be
            excluded. If you are a consumer, your mandatory rights under Dutch and EU law apply in
            full and nothing in these terms takes them away.
          </p>
        </Section>

        <Section title="Availability of the site">
          <p>
            We aim to keep sunbedly up and current, and we do not promise it will never be down or
            never be out of date. We can change or withdraw parts of it. If a change matters to
            people who rely on us, we say so on the page rather than in a version number.
          </p>
        </Section>

        <Section title="Changes to these terms">
          <p>
            If we change these terms, the date at the top changes with them. For clubs that use the
            dashboard, we give notice by email before a material change takes effect.
          </p>
        </Section>

        <Section title="Law and disputes">
          <p>
            Dutch law applies and the Dutch courts have jurisdiction. If you are a consumer living
            elsewhere in the EU, this does not deprive you of the protection of your own country's
            mandatory consumer law, nor of your right to bring a case in your own country.
          </p>
          <p>
            Before any of that: email {mail}. A person reads it, and almost everything gets sorted
            there.
          </p>
        </Section>
      </main>
      <Footer />
    </div>
  );
}
